import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { ministries } from '../mock';
import { useSiteInfo } from '../context/SiteSettingsContext';

const UMC_LOGO = '/images/home/umc-logo.svg';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDesktop, setOpenDesktop] = useState(null); // key of open desktop dropdown
  const [openMobile, setOpenMobile] = useState({}); // map of key -> bool
  const location = useLocation();
  const { email } = useSiteInfo();

  const ministryChildren = [
    { path: '/ministries', label: 'All Ministries' },
    ...ministries
      .filter((m) => m.slug !== 'social-concern')
      .map((m) => ({ path: `/ministries/${m.slug}`, label: m.shortName })),
  ];

  const navLinks = [
    { key: 'home', path: '/', label: 'Home' },
    {
      key: 'about',
      path: '/about',
      label: 'About',
      children: [
        { path: '/about', label: 'About Us' },
        { path: '/about/pastor', label: 'Our Pastor' },
        { path: '/about/committee', label: 'Church Committee' },
        { path: '/about/brc', label: 'BRC' },
      ],
    },
    {
      key: 'ministry',
      path: '/ministries',
      label: 'Ministry',
      children: [
        ...ministryChildren,
        { path: '/resources', label: 'Kerygma' },
      ],
    },
    {
      key: 'events',
      path: '/events',
      label: 'Events',
      children: [
        { path: '/events', label: 'All Events' },
        { path: '/gallery', label: 'Gallery' },
      ],
    },
    { key: 'nehemiah', path: '/nehemiah-project', label: 'Nehemiah Project' },
    { key: 'contact', path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (link) =>
    location.pathname === link.path ||
    location.pathname.startsWith(link.path + '/') ||
    (link.children || []).some((c) => isActive(c.path));

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0" data-testid="header-logo-link">
            <img
              src={UMC_LOGO}
              alt="United Methodist Church logo"
              className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
              width="48"
              height="48"
              decoding="async"
            />
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-slate-800 truncate">Christ Methodist Church</h1>
              <p className="text-[10px] sm:text-xs text-slate-600 truncate">Kothanur, Bangalore</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              if (link.children) {
                const isOpen = openDesktop === link.key;
                return (
                  <div
                    key={link.key}
                    className="relative"
                    onMouseEnter={() => setOpenDesktop(link.key)}
                    onMouseLeave={() => setOpenDesktop(null)}
                  >
                    <button
                      type="button"
                      data-testid={`nav-${link.key}-trigger`}
                      onClick={() => setOpenDesktop(isOpen ? null : link.key)}
                      className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isGroupActive(link)
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      {link.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div
                        className="absolute left-0 top-full pt-2 w-60"
                        data-testid={`nav-${link.key}-dropdown`}
                      >
                        <div className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                          {link.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              data-testid={`nav-submenu-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                              onClick={() => setOpenDesktop(null)}
                              className={`block px-4 py-3 text-sm transition-colors ${
                                isActive(child.path)
                                  ? 'bg-blue-50 text-blue-700 font-semibold'
                                  : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={link.key}
                  to={link.path}
                  data-testid={`nav-${link.key}-link`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(link.path)
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Email */}
          <div className="hidden lg:block">
            <a
              href={`mailto:${email}`}
              className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
            >
              {email}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <nav className="px-4 py-4 space-y-2">
            {navLinks.map((link) => {
              if (link.children) {
                const isOpen = !!openMobile[link.key];
                return (
                  <div key={link.key}>
                    <button
                      type="button"
                      onClick={() => setOpenMobile((s) => ({ ...s, [link.key]: !s[link.key] }))}
                      data-testid={`mobile-nav-${link.key}-trigger`}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        isGroupActive(link) ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {link.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="ml-3 mt-1 border-l-2 border-blue-100 pl-3 space-y-1">
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setIsMenuOpen(false)}
                            data-testid={`mobile-nav-submenu-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            className={`block px-4 py-2 rounded-lg text-sm transition-colors ${
                              isActive(child.path) ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={link.key}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-${link.key}-link`}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path) ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <a
              href={`mailto:${email}`}
              className="block px-4 py-3 text-sm text-slate-600 hover:text-blue-600"
            >
              {email}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
